import React, { useState } from 'react';
import { useQueryClient } from 'react-query';

import dayjs from 'dayjs';
import styled from 'styled-components';

import { deleteSnapshot } from 'apis/SnapshotsAPI';
import ConfirmModal from 'common/Modal/ConfirmModal';
import { useCommunityInfoContext } from 'components/CommunityInfo/CommunityInfoContext';
import { BROCHURE_AND_DOCS_QUERY_KEY } from 'components/CommunityInfo/general/documents/constants';
import { useRootContext } from 'components/layout/RootContext';
import Button from 'components/lib/Button';
import { isManagedBySuperuser } from 'helpers/permissions';
import { useAppSelector } from 'store/hook';

import { getSnapshotStatusLabel, resolveSnapshotActor } from './helpers';
import { useSnapshotContext } from './SnapshotContext';
import { STATUS } from '../../constants';
import DataPropagationConfirmModal from '../Modal/DataPropagationConfirmModal';

const DeleteButton = styled(Button)`
  && {
    width: 28px !important;
    height: 28px !important;
    display: flex;
    justify-content: center;
    align-items: center;
    border-radius: 4px;
    box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.1);
    border: solid 1px var(--line-gray);
    background-color: var(--white);
    padding: 0 !important;
  }
`;

const Icon = styled.span`
  display: block;
  width: 16px;
  height: 16px;
  background-image: url('/icons/delete-icon.svg');
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;
`;

const ModalTitle = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 14px;
`;

const SubTitle = styled.div`
  font-size: 14px;
  font-family: var(--font-regular);
  font-weight: 500;
  color: var(--text-secondary);
`;

interface Props {
  disabled: boolean;
}

const SnapshotDelete = ({ disabled }: Props) => {
  const { showMessage } = useRootContext();
  const { currentUser } = useAppSelector((state) => state.auth);
  const { snapshot, refetch } = useSnapshotContext();
  const { communityInfoRefetch } = useCommunityInfoContext();
  const queryClient = useQueryClient();

  const [open, setOpen] = useState<boolean>(false);
  const [status, setStatus] = useState(STATUS.IDLE);
  const [showDataPropagationConfirmModal, setShowDataPropagationConfirmModal] = useState<boolean>(false);

  const dateFormat = isManagedBySuperuser(currentUser) ? 'MMM DD YYYY' : 'MMM YYYY';
  const snapshotActor = snapshot && resolveSnapshotActor(currentUser, snapshot);

  const handleConfirmationCheck = () => {
    if (snapshot?.shared) {
      setShowDataPropagationConfirmModal(true);
    } else {
      handleDelete();
    }
  };

  const handleDelete = async () => {
    if (!snapshot?.id) return;

    setStatus(STATUS.LOADING);
    try {
      await deleteSnapshot(snapshot.id).then(() => {
        refetch();
        communityInfoRefetch();
        queryClient.invalidateQueries(BROCHURE_AND_DOCS_QUERY_KEY);
        setOpen(false);
        setShowDataPropagationConfirmModal(false);
        setStatus(STATUS.LOADED);
        showMessage('success', 'Snapshot deleted');
      });
    } catch (e) {
      setStatus(STATUS.FAILURE);
      showMessage('error', 'Trouble deleting snapshot.');
      console.error('Trouble deleting snapshot.', e);
    }
  };

  const DeleteSnapshotModalTitle = () => (
    <ModalTitle>
      <div>{`Delete ${getSnapshotStatusLabel(snapshot)} data for ${dayjs(snapshot?.date).format(dateFormat)}?`}</div>
      {snapshotActor && <SubTitle>Added {snapshotActor}</SubTitle>}
    </ModalTitle>
  );

  return (
    <div>
      <DeleteButton
        data-test-id="cd-delete-snapshot-btn"
        disabled={disabled}
        icon={<Icon />}
        onClick={() => setOpen(true)}
      />
      <ConfirmModal
        open={open}
        title={
          <div data-test-id="cd-community-snapshot-delete-modal-title">
            <DeleteSnapshotModalTitle />
          </div>
        }
        description={<div style={{ padding: '20px 0px' }}>Are you sure you want to delete this data?</div>}
        confirmLabel={'Delete'}
        loading={status === STATUS.LOADING}
        confirmButtonDataTestId="cd-community-snapshot-delete-confirm"
        cancelButtonDataTestId="cd-community-snapshot-delete-cancel"
        onConfirm={handleConfirmationCheck}
        onCancel={() => setOpen(false)}
      />
      {showDataPropagationConfirmModal && (
        <DataPropagationConfirmModal
          open={showDataPropagationConfirmModal}
          onConfirm={handleDelete}
          onCancel={() => setShowDataPropagationConfirmModal(false)}
        />
      )}
    </div>
  );
};

export default SnapshotDelete;
